/**
 * The Slideshow's pause and play buttons (THEME.md §7.4, §11.2; D-122 CA8, T-195; DESIGN.md › Components › Page
 * sections › Hero). Horizon draws two buttons in a slideshow that plays by itself, Pause and Play, and shows the one
 * that acts by the slideshow's `paused` attribute; okt-home.css hides the other, so it is no Tab stop. This module,
 * with Horizon's own slideshow API:
 * - moves the focus to the button that shows when the one that had it hides (Pause or Play by keyboard, click or
 *   tap, or a swipe that pauses), so the focus never stays on a hidden button;
 * - for visitors who reduce motion, starts the slideshow paused, showing Play (also when they turn it on during a
 *   visit): it doesn't turn by itself, Play starts it, and the arrows and swiping still step through the slides;
 * - (T-197, WAI-ARIA's carousel pattern) stops the turning while the keyboard focus is in the slideshow, until it
 *   leaves (as Horizon does while the pointer hovers), and keeps Horizon's live region quiet while the slides turn by
 *   themselves (aria-live off; polite when paused or while the keyboard is in it, so Previous and Next are read);
 * - (T-197) makes the hidden slides of a full-frame Slideshow inert, so only the slide that shows has Tab stops (its
 *   link); Horizon marks them aria-hidden only.
 * Loaded on every page by snippets/okt-tokens.liquid; it does nothing where there is no Slideshow section.
 */

const SLIDESHOWS = '.slideshow-section slideshow-component[autoplay]';
const FULL_FRAME = '.slideshow-section slideshow-component:not(.slideshow--with-hints)';
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** @type {WeakSet<Element>} */
const known = new WeakSet();

/** @type {WeakSet<Element>} */
const framed = new WeakSet();

/** Slideshows that hold the keyboard focus. @type {WeakSet<Element>} */
const keyboardIn = new WeakSet();

/**
 * @param {Element} slideshow
 */
function updateLive(slideshow) {
  const turning = !slideshow.hasAttribute('paused') && !keyboardIn.has(slideshow);
  slideshow.setAttribute('aria-live', turning ? 'off' : 'polite');
}

const focusKeeper = new MutationObserver((records) => {
  for (const slideshow of new Set(records.map((record) => record.target))) {
    if (!(slideshow instanceof HTMLElement)) continue;
    updateLive(slideshow);

    const pause = slideshow.querySelector(':scope > slideshow-controls .icon-pause');
    const play = slideshow.querySelector(':scope > slideshow-controls .icon-play');
    if (!(pause instanceof HTMLElement) || !(play instanceof HTMLElement)) continue;

    const [hidden, shown] = slideshow.hasAttribute('paused') ? [pause, play] : [play, pause];
    if (document.activeElement === hidden) shown.focus({ preventScroll: true });
  }
});

/**
 * @param {Element} slide
 */
function syncInert(slide) {
  if (slide instanceof HTMLElement && slide.localName === 'slideshow-slide') {
    slide.inert = slide.getAttribute('aria-hidden') === 'true';
  }
}

const inertKeeper = new MutationObserver((records) => records.forEach((record) => syncInert(/** @type {Element} */ (record.target))));

/**
 * @param {ParentNode} root
 */
function setUp(root) {
  for (const slideshow of root.querySelectorAll(FULL_FRAME)) {
    if (framed.has(slideshow)) continue;
    framed.add(slideshow);

    slideshow.querySelectorAll('slideshow-slide').forEach(syncInert);
    inertKeeper.observe(slideshow, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
  }

  for (const slideshow of root.querySelectorAll(SLIDESHOWS)) {
    if (known.has(slideshow)) continue;
    known.add(slideshow);
    const api = /** @type {any} */ (slideshow);

    focusKeeper.observe(slideshow, { attributes: true, attributeFilter: ['paused'] });
    if (reducedMotion.matches) api.pause();

    slideshow.addEventListener('focusin', (event) => {
      if (keyboardIn.has(slideshow) || !(event.target instanceof Element) || !event.target.matches(':focus-visible')) return;
      keyboardIn.add(slideshow);
      api.suspend();
      updateLive(slideshow);
    });
    slideshow.addEventListener('focusout', (event) => {
      if (!keyboardIn.has(slideshow) || (event.relatedTarget instanceof Node && slideshow.contains(event.relatedTarget))) return;
      keyboardIn.delete(slideshow);
      api.resume();
      updateLive(slideshow);
    });
    updateLive(slideshow);
  }
}

customElements.whenDefined('slideshow-component').then(() => {
  setUp(document);

  // The theme editor renders a section again on every change
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) setUp(event.target);
  });

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    for (const slideshow of document.querySelectorAll(SLIDESHOWS)) /** @type {any} */ (slideshow).pause();
  });
});
